import { type FC, type KeyboardEvent, useCallback, useState } from "react";

import { Button } from "../common/Button";
import { SIGNAL_HEX } from "../common/colorTokens";
import { Modal } from "../common/Modal";
import { ChannelTag } from "./ChannelTag";
import { RetroEmpty, RetroError, SectionLabel } from "./retro";
import { type OrgChannelSummary, useOrgChannels } from "./useOrgChannels";

interface ChannelPickerDialogProps {
  instanceId: string;
  /** Channel IDs the instance is already a member of (to exclude from join list). */
  memberChannelIds: Set<string>;
  /** Display name of the shared channel the instance is currently in, if any.
   *  An actor lives in at most one shared channel — joining or creating
   *  another one REPLACES the current membership (the backend swaps and emits
   *  leave events), so the dialog must say so instead of implying "add". */
  currentSharedChannelName?: string | null;
  onClose: () => void;
  onJoined: (channel: OrgChannelSummary) => void;
}

const getResponseErrorMessage = (data: unknown, fallback: string) =>
  typeof data === "object" &&
  data !== null &&
  "message" in data &&
  typeof data.message === "string"
    ? data.message
    : fallback;

export const ChannelPickerDialog: FC<ChannelPickerDialogProps> = ({
  instanceId,
  memberChannelIds,
  currentSharedChannelName,
  onClose,
  onJoined,
}) => {
  const { channels: allChannels, loading: loadingChannels } = useOrgChannels();
  const [query, setQuery] = useState("");
  const [joining, setJoining] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const joinableChannels = allChannels.filter(
    (ch) =>
      ch.deletedAt == null &&
      !memberChannelIds.has(ch.id) &&
      (ch.archivedAt == null ||
        (ch.provenance === "manual" &&
          ch.archivedReason === "dormant_memberless")) &&
      (query === "" ||
        ch.displayName.toLowerCase().includes(query.toLowerCase())),
  );

  const handleJoin = useCallback(
    async (channelId: string) => {
      setJoining(channelId);
      setError(null);
      try {
        const res = await fetch(`/user/instances/${instanceId}/channels`, {
          body: JSON.stringify({ channelId }),
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          method: "POST",
        });
        if (!res.ok) {
          const data: unknown = await res.json().catch(() => ({}));
          throw new Error(getResponseErrorMessage(data, `HTTP ${res.status}`));
        }
        const ch = allChannels.find((c) => c.id === channelId);
        if (ch) {
          onJoined(ch);
        }
        onClose();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError.message
            : "Failed to join channel",
        );
      } finally {
        setJoining(null);
      }
    },
    [allChannels, instanceId, onJoined, onClose],
  );

  const handleCreate = useCallback(async () => {
    if (creating) {
      return;
    }
    const name = newChannelName.trim().toLowerCase().replace(/\s+/g, "-");
    if (!name) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const createRes = await fetch(
        `/user/instances/${instanceId}/channels/create-and-join`,
        {
          body: JSON.stringify({ displayName: name }),
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          method: "POST",
        },
      );
      if (!createRes.ok) {
        const data: unknown = await createRes.json().catch(() => ({}));
        throw new Error(
          getResponseErrorMessage(data, `HTTP ${createRes.status}`),
        );
      }
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      const newChannelRaw = (await createRes.json()) as {
        id: string;
        displayName: string;
        createdAt: string;
        kind: "shared";
        memberAvatarSeeds: string[];
        memberCount: number;
        organizationId: string;
        ownerActorId: null;
        provenance: "issue_workspace" | "manual" | "teams_bind";
        environmentStatus: null;
      };
      const newChannel: OrgChannelSummary = {
        ...newChannelRaw,
        accentColor: SIGNAL_HEX.accent,
        activeTeamsBindingId: null,
        activeTeamsConversationId: null,
        activeTeamsConversationLabel: null,
        environmentStatus: null,
        hasDetachedTeamsBinding: false,
        lastActivityAt: null,
        teamsBindingChannelKind: null,
      };
      onJoined(newChannel);
      onClose();
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to create channel",
      );
    } finally {
      setCreating(false);
    }
  }, [creating, newChannelName, instanceId, onJoined, onClose]);

  const handleCreateKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        void handleCreate();
      }
    },
    [handleCreate],
  );

  const replacing = Boolean(currentSharedChannelName);

  const existingChannelContent = (() => {
    if (loadingChannels) {
      return (
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-violet-300/60">
          [ LOADING… ]
        </p>
      );
    }
    if (joinableChannels.length === 0) {
      return (
        <RetroEmpty
          label={query ? "NO MATCHES" : "NO OTHERS"}
          sub={
            query
              ? "no channels match this search"
              : "this instance is in every available channel"
          }
        />
      );
    }
    return (
      <ul className="max-h-44 space-y-1 overflow-y-auto">
        {joinableChannels.map((ch) => (
          <li
            key={ch.id}
            className="flex items-center justify-between gap-2 border bg-slate-50 px-3 py-1.5 transition-colors dark:bg-slate-900/40"
            style={{ borderColor: `${ch.accentColor}33` }}
          >
            <ChannelTag channel={ch} className="text-[10px]" />
            {ch.archivedAt && (
              <span className="font-mono text-[9px] uppercase tracking-wider text-amber-600 dark:text-amber-300">
                dormant · {ch.archivedReason ?? "archived"} ·{" "}
                {new Date(ch.archivedAt).toLocaleDateString()}
              </span>
            )}
            <Button
              size="sm"
              variant="secondary"
              loading={joining === ch.id}
              loadingText="Joining…"
              onClick={() => void handleJoin(ch.id)}
            >
              {(() => {
                if (ch.archivedAt) {
                  return replacing ? "Restore & switch" : "Restore & join";
                }
                return replacing ? "Switch" : "Join";
              })()}
            </Button>
          </li>
        ))}
      </ul>
    );
  })();

  return (
    <Modal
      title={replacing ? "Change Shared Channel" : "Add Channel"}
      subtitle={
        replacing
          ? `Joining or creating a channel moves this actor out of #${currentSharedChannelName}`
          : "Join an existing channel or create a new one"
      }
      onClose={onClose}
      widthClass="max-w-sm"
      footer={
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {error && <RetroError message={error} />}

        {/* Create new */}
        <div>
          <SectionLabel label="CREATE NEW" accent="emerald" className="mb-2" />
          <div className="flex gap-2">
            <input
              id="new-channel-name"
              type="text"
              autoFocus
              value={newChannelName}
              onChange={(e) => setNewChannelName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              placeholder="channel-name"
              className="flex-1 border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-600 dark:focus:border-emerald-400/60 dark:focus:bg-slate-900 dark:focus:ring-emerald-400/30"
            />
            <Button
              type="button"
              size="sm"
              loading={creating}
              loadingText="Creating..."
              disabled={!newChannelName.trim()}
              onClick={() => void handleCreate()}
            >
              Create
            </Button>
          </div>
        </div>

        {/* Join existing */}
        <div>
          <SectionLabel
            label={replacing ? "SWITCH TO EXISTING" : "JOIN EXISTING"}
            accent="violet"
            meta={
              joinableChannels.length > 0 ? (
                <span className="tabular-nums">
                  {joinableChannels.length} available
                </span>
              ) : undefined
            }
            className="mb-2"
          />
          <input
            id="channel-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search channels…"
            className="mb-2 w-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-900/60 dark:text-white dark:placeholder:text-slate-600 dark:focus:border-violet-400/60 dark:focus:bg-slate-900 dark:focus:ring-violet-400/30"
          />
          {existingChannelContent}
        </div>
      </div>
    </Modal>
  );
};
